import type { FlightCardData } from '@massilia/queries'

import { useMemo } from 'react'

import { boundsToRange } from './filter-url-params'
import { getDurationBounds, getPriceBounds } from './filter-utils'
import { useFiltersState } from './filters-state-context'

/**
 * Vrai dès qu'un filtre de la sidebar RESSERRE la liste, donc dès que
 * « Réinitialiser les filtres » a quelque chose à faire.
 *
 * Partagé par la sidebar et la modale mobile : les deux affichent le même
 * bouton, ils doivent s'activer et se griser ensemble. Lu depuis le contexte,
 * il suit le brouillon de la modale comme les filtres réellement appliqués.
 */
export function useHasActiveFilters(offers: FlightCardData[]): boolean {
  const { filters } = useFiltersState()
  const priceBounds = useMemo(() => getPriceBounds(offers), [offers])
  const durationBounds = useMemo(() => getDurationBounds(offers), [offers])

  return useMemo(() => {
    // Un intervalle est actif dès qu'il RESSERRE la plage réelle. Deux bugs
    // corrigés ici : exiger les deux bornes laissait le bouton « Réinitialiser »
    // grisé alors qu'un plafond posé par le chat filtrait bel et bien ; et la
    // comparaison croisée (`max > bounds.min || min < bounds.max`) était vraie
    // dans presque tous les cas, y compris sans aucun filtre.
    const priceRange = boundsToRange(filters.filterPriceMin, filters.filterPriceMax)
    const isPriceFilterActive =
      !!priceRange && (priceRange.min > priceBounds.min || priceRange.max < priceBounds.max)

    const durationRange = boundsToRange(filters.filterDurationMin, filters.filterDurationMax)
    const isDurationFilterActive =
      !!durationRange &&
      (durationRange.min > durationBounds.min || durationRange.max < durationBounds.max)
    const isTimeFilterActive = filters.filterTimeRanges
      ? filters.filterTimeRanges.length > 0
      : false
    const isAirlinesFilterActive = filters.filterAirlines
      ? filters.filterAirlines.length > 0
      : false

    return (
      isPriceFilterActive ||
      isDurationFilterActive ||
      isTimeFilterActive ||
      isAirlinesFilterActive ||
      (filters.filterStops ?? 'all') !== 'all' ||
      (filters.filterPayment ?? 'all') !== 'all' ||
      // `'0'` est la valeur neutre du filtre bagage (cf. le `.default('0')` des
      // deux schémas de search) : la comparer à `'1'` gardait le bouton
      // « Réinitialiser » actif en permanence, sans qu'aucun filtre soit posé.
      (filters.filterLuggage ?? '0') !== '0'
    )
  }, [filters, priceBounds, durationBounds])
}
